import React, { useEffect, useRef, useState } from 'react'
import WebComponentContainer from '../generic/WebComponentContainer'
import useEventBus from '../generic/useEventBus'
import CookieHelper from '../../utils/CookieHelper'
import { BrowserUtils } from '../../utils/siteUtils'

const ContactComponentWrapper = ({
  contactPageIsLoaded,
  updateGlobalState,
  year,
  make,
  model,
  trim,
  color,
  transmission,
  engine,
  drivetrain,
  vinResults,
  mileage,
  selectedOptionIds,
  unselectedOptionIds,
  firstName,
  lastName,
  email,
  phone,
  zip,
  referrerUrl,
  offerCode,
  browserType,
  lnxCode,
  dealerName,
  showCustomerFormValidation,
  entry,
  acceptsRv,
  dealerGuid,
  cobrandingInfo = {},
  isAssigned,
  voiVin,
  recaptchaDisabled,
  includePixall,
  translateError,
  emailCustomerCheckbox,
  dealerRepId,
  inviteId,
  allowIneligibleVehicleSubmission,
  showLegalAcceptance,
  showLegalText,
  authToken,
  isConsumerOffer,
  showAdditionalComment,
  additionalComment,
  onCommentChange,
  isAdmin,
  customId,
  theme,
}) => {
  const contactStateHydratorRef = useRef(null)
  const [showLoader, setShowLoader] = useState(true)
  const [pixallId, setPixallId] = useState(null)
  const [pixallRealId, setPixallRealId] = useState(null)
  const [replacementVehicleOptionsProp, setReplacementVehicleOptionsProp] = useState(null)

  useEventBus('FormValidated', payload => {
    if (payload.form === 'customer-contact') {
      updateGlobalState({ formCustomerValid: { name: payload.form, isValid: payload.isValid } })
    }
  })

  useEventBus('QuestionAnswered', event => {
    const { answered, contactInfo } = event
    if (answered?.section === 'contactInfo') {
      updateGlobalState({
        firstName: contactInfo?.firstName,
        lastName: contactInfo?.lastName,
        email: contactInfo?.email,
        phone: contactInfo?.phone,
        zip: contactInfo?.zip
      })
    } else if (answered?.section === 'other') {
      const [question] = Object.entries(answered)
      const [questionId, answer] = question
      if (questionId === 'qx/UserComments') {
        onCommentChange(answer)
      }
    }

    const dataLayer = BrowserUtils.getDataLayer()
    dataLayer.contactInfo = contactInfo
  })

  useEventBus('OfferSubmit', () => {
    updateGlobalState({ termsAccepted: true, offerError: [], offerErrorsFromWebComp: [] })
  })

  useEffect(() => {
    setShowLoader(true)
  }, [contactPageIsLoaded])

  // triggers saving the prop value into state when contact component DOM node is attached
  // doing this in two steps (DOM mounted useEffect -> save to state -> useEffect -> save to component)
  // because doing it all at once didn't write the prop correctly due to render timing
  useEffect(() => {
    setReplacementVehicleOptionsProp({
      replacementVehicleMakes: cobrandingInfo.replacementVehicleMakes,
      replacementVehicleModels: cobrandingInfo.replacementVehicleModels,
      replacementVehicleTypes: cobrandingInfo.replacementVehicleTypes
    })
  }, [contactStateHydratorRef?.current])

  // this set the replacementVehicleOptions prop to the value stored in component state
  // complex objects cannot be passed directly into the component as an attribute
  // they need to be "attached" to the DOM node (at least until react 19 maybe) via hook
  useEffect(() => {
    if (cobrandingInfo && contactStateHydratorRef?.current) {
      contactStateHydratorRef.current.replacementVehicleOptions = replacementVehicleOptionsProp
    }
  }, [replacementVehicleOptionsProp])

  useEffect(() => {
    if (includePixall) {
      const pixallIdCookie = CookieHelper.get('abc') || CookieHelper.get('pxa_id') || ''
      setPixallId(pixallIdCookie && pixallIdCookie.match(/[^A-Z0-9]/gi) ? '' : pixallIdCookie)
      const pixallRealId = CookieHelper.get('pxa_realid') || ''
      setPixallRealId(pixallRealId)
    }
  }, [includePixall])

  return (
    <WebComponentContainer pageLoadingComplete={contactPageIsLoaded} withLoader={showLoader} location={'contact'} theme={theme}>
      <contact-state-hydrator
        year-id={year.yearId}
        year={year.displayName}
        make-id={make.makeId}
        make={make.displayName}
        model-id={model.modelId}
        model={model.displayName}
        trim-id={trim.trimId}
        trim={trim.displayName}
        color-id={color.colorId}
        color={color.displayName}
        transmission-id={transmission.transmissionId}
        transmission={transmission.displayName}
        engine-id={engine.engineId}
        engine={engine.displayName}
        drivetrain-id={drivetrain.drivetrainId}
        drivetrain={drivetrain.displayName}
        vin={vinResults ? vinResults.vin : null}
        mileage={mileage}
        selected-option-ids={selectedOptionIds}
        unselected-option-ids={unselectedOptionIds}
        contact-first-name={firstName}
        contact-last-name={lastName}
        contact-phone={phone}
        contact-email={email}
        contact-zip={zip}
        referrer-url={referrerUrl}
        offer-code={offerCode}
        browser-type={browserType}
        entry={entry}
        lnx-code={lnxCode}
        dealer-name={dealerName}
        replacement-is-looking-for-replacement-vehicle={true}
        show-form-validation={showCustomerFormValidation}
        accepts-rv={acceptsRv}
        dealer-guid={dealerGuid}
        cobranding-group-id={cobrandingInfo && cobrandingInfo.groupId ? cobrandingInfo.groupId : ''}
        voi-vin={voiVin}
        answer-value-for-lead-type-no={isAssigned ? 'OptOut' : 'CashOffer'}
        ref={contactStateHydratorRef}
        notify-replacement-vehicle-answer-change={true}
        include-pixall={includePixall}
        pixall-id={pixallId}
        pixall-real-id={pixallRealId}
        email-customer-checkbox={emailCustomerCheckbox}
        dealer-rep-id={dealerRepId}
        invite-id={inviteId}
        allow-ineligible-vehicle-submission={allowIneligibleVehicleSubmission}
        show-legal-acceptance={showLegalAcceptance}
        show-legal-text={showLegalText}
        auth-token={authToken}
        is-consumer-offer={!!isConsumerOffer}
        show-additional-comment={showAdditionalComment}
        contact-additional-comment={additionalComment}
        is-dealer={isAdmin}
        custom-id={customId}
        theme={theme}>
        {translateError !== '' && <error-message-display error-message={translateError} />}
        <contact-information>
          <captcha-field captcha-ready={!recaptchaDisabled} slot="captcha">
            <div id="captcha"></div>
          </captcha-field>
        </contact-information>
      </contact-state-hydrator>
    </WebComponentContainer>
  )
}

export default ContactComponentWrapper
